export type TableThemeId =
  | 'classicGreen'
  | 'basicDark'
  | 'texasOrange'
  | 'burgundy'
  | 'softPurple'
  | 'brighterGreen'
  | 'sunshine'
  | 'neon';

export type TableTheme = {
  id: TableThemeId;
  name: string;
  /** Outer felt ring colour — applied as `tableOvalStyle.backgroundColor`. */
  feltBase: string;
  /** Five radial-gradient stops (light → dark) used for the inner felt surface. */
  feltGradient: [string, string, string, string, string];
  /** Border-glow accent colour. Falls back to gold when omitted. */
  glowColor?: string;
  /** Swatch colours used to render the theme picker card preview. */
  swatch: { outer: string; felt: string; ring: string };
};

export const DEFAULT_THEME_ID: TableThemeId = 'classicGreen';

export const TABLE_THEMES: Record<TableThemeId, TableTheme> = {
  classicGreen: {
    id: 'classicGreen',
    name: 'Classic Green',
    feltBase: '#143021',
    feltGradient: ['#2a6b45', '#1d5535', '#174a2d', '#0f3820', '#0a2a18'],
    swatch: { outer: '#0a2418', felt: '#1d5535', ring: '#2a6b45' }
  },
  basicDark: {
    id: 'basicDark',
    name: 'Basic Dark',
    feltBase: '#1a1d24',
    feltGradient: ['#2c3038', '#252830', '#1d2028', '#171a22', '#0f1218'],
    swatch: { outer: '#0d1118', felt: '#252830', ring: '#3a3f48' }
  },
  texasOrange: {
    id: 'texasOrange',
    name: 'Texas Orange',
    feltBase: '#5a2d0e',
    feltGradient: ['#d97a22', '#bb6516', '#94500e', '#683806', '#3a1d02'],
    swatch: { outer: '#3a1d02', felt: '#bb6516', ring: '#e08a30' }
  },
  burgundy: {
    id: 'burgundy',
    name: 'Burgundy',
    feltBase: '#3d0f1b',
    feltGradient: ['#9c2a3b', '#7a1d2c', '#601521', '#430d18', '#26050d'],
    swatch: { outer: '#26050d', felt: '#7a1d2c', ring: '#a83244' }
  },
  softPurple: {
    id: 'softPurple',
    name: 'Soft Purple',
    feltBase: '#2a2548',
    feltGradient: ['#7468b8', '#5e52a0', '#4a4188', '#37306a', '#1f1c3a'],
    swatch: { outer: '#1f1c3a', felt: '#5e52a0', ring: '#8579c8' }
  },
  brighterGreen: {
    id: 'brighterGreen',
    name: 'Brighter Green',
    feltBase: '#1d4d3f',
    feltGradient: ['#4ea389', '#3f8f78', '#327864', '#256252', '#15403a'],
    swatch: { outer: '#15403a', felt: '#3f8f78', ring: '#5cb39a' }
  },
  sunshine: {
    id: 'sunshine',
    name: 'Sunshine',
    feltBase: '#5e4a0c',
    feltGradient: ['#e6c249', '#cca735', '#a98624', '#866618', '#5a440c'],
    swatch: { outer: '#5a440c', felt: '#cca735', ring: '#f0d05a' }
  },
  neon: {
    id: 'neon',
    name: 'Neon',
    feltBase: '#0a0e26',
    feltGradient: ['#1c3a78', '#152d62', '#10224c', '#0a1633', '#06091e'],
    glowColor: '#3ed3ff',
    swatch: { outer: '#06091e', felt: '#152d62', ring: '#3ed3ff' }
  }
};

export const TABLE_THEME_ORDER: TableThemeId[] = [
  'classicGreen',
  'basicDark',
  'texasOrange',
  'burgundy',
  'softPurple',
  'brighterGreen',
  'sunshine',
  'neon'
];

export const getTableTheme = (id: string | undefined | null): TableTheme => {
  if (id && id in TABLE_THEMES) return TABLE_THEMES[id as TableThemeId];
  return TABLE_THEMES[DEFAULT_THEME_ID];
};
